import { useEffect, useState } from "react";
import { useToast } from "../../contexts/ToastContext";
import { ethers } from "ethers";

import {
  useWriteContract,
  useWaitForTransactionReceipt,
  useBalance,
  useAccount,
} from "wagmi";

import presale_ABI from "../../contracts/presale_abi.json";

import usdt_abi from "../../contracts/usdt_abi.json";

const presale_address = "0xe420ff531abecdc1d4feb4aa0c825a31bda6c84c";
const usdt_address = "0x09929a56f7a28ca767b26c56c8ec1424a3a58f19";

const PresaleRound = ({ round, isWalletConnected }) => {
  const { showToast } = useToast();
  const account = useAccount();

  const [amount, setAmount] = useState("");
  const [error, setError] = useState("");

  const { data: hash, isPending, writeContract } = useWriteContract();

  const { isLoading: isConfirming, isSuccess: isConfirmed } =
    useWaitForTransactionReceipt({
      hash,
    });

  const progressPercentage = (round.soldTokens / round.totalTokens) * 100;

  const usdt_balence = useBalance({
    address: account.address,
    token: usdt_address,
  });

  const handleBuy = async () => {
    // Check if amount is valid
    if (amount <= 0) {
      showToast("Error", "Amount must be greater than 0.", "danger");
      return;
    }

    // Calculate the total cost
    const totalCost = ethers.parseUnits(
      (Number(amount) * round.price).toFixed(2),
      18
    );

    if (usdt_balence.data.value < totalCost) {
      showToast("Warning", "You don't have enough USDT", "warning");
      return;
    }

    // Approve the USDT contract
    writeContract({
      address: usdt_address,
      abi: usdt_abi,
      functionName: "approve",
      args: [presale_address, totalCost],
    });

    // Buy tokens in the presale
    writeContract({
      address: presale_address,
      abi: presale_ABI,
      functionName: "buyTokens",
      args: [totalCost],
    });
  };

  const handleAmountChange = (e) => {
    let value = e.target.value;

    // Handle cases like ".1" by converting to "0.1"
    if (value.startsWith(".")) {
      value = "0" + value;
    }

    if (value === "") {
      setAmount("");
      setError("");
    } else if (isNaN(value) || Number(value) < 0) {
      setError("Please enter a valid positive number");
      showToast("Error", "Please enter a valid positive number", "danger");
    } else if (Number(value) > round.totalTokens - round.soldTokens) {
      setError(
        `Maximum available tokens: ${(
          round.totalTokens - round.soldTokens
        ).toLocaleString()}`
      );
      showToast(
        "Error",
        `Maximum available tokens: ${(
          round.totalTokens - round.soldTokens
        ).toLocaleString()}`,
        "danger"
      );
      setAmount(value);
    } else {
      setAmount(value);
      setError("");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return "bg-success";
      case "upcoming":
        return "bg-primary";
      case "ended":
        return "bg-secondary";
      default:
        return "bg-secondary";
    }
  };

  const getCardBackgroundColor = (status) => {
    switch (status) {
      case "active":
        return "bg-transparent bg-blur border-success";
      case "upcoming":
        return "bg-transparent bg-blur border-primary";
      case "ended":
        return "bg-transparent bg-blur border-secondary";
      default:
        return "bg-transparent bg-blur";
    }
  };

  const getProgressBarColor = (status) => {
    switch (status) {
      case "active":
        return "bg-success";
      case "upcoming":
        return "bg-primary";
      case "ended":
        return "bg-secondary";
      default:
        return "bg-secondary";
    }
  };

  const flowLightStyle = `
  @keyframes flowLight {
    0% {
      transform: translateX(-100%);
    }
    100% {
      transform: translateX(200%);
    }
  }
`;

  useEffect(() => {
    if (isConfirmed) {
      showToast("Info", "Transaction Confirmed", "success");
    } else if (hash && !isPending && !isConfirming) {
      showToast("Warning", "Transaction Un-Confirmed", "warning");
    }
  }, [isConfirmed, isConfirming]);

  useEffect(() => {
    console.log("hash : ", hash);
    console.log("isPending : ", isPending);
    console.log("isConfirming : ", isConfirming);
    console.log("isConfirmed : ", isConfirmed);
  }, [hash, isPending, isConfirming, isConfirmed]);

  return (
    <>
      <style>{flowLightStyle}</style>
      <div className={`card shadow ${getCardBackgroundColor(round.status)}`}>
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h3 className="card-title fw-bold text-white">{round.name}</h3>
            <span
              className={`badge ${getStatusColor(round.status)} text-white`}
            >
              {round.status.charAt(0).toUpperCase() + round.status.slice(1)}
            </span>
          </div>

          <div className="mb-3">
            <p className="text-secondary small">Price per token</p>
            <p className="fw-semibold text-light">{round.price} USDT - ERC20</p>
          </div>

          <div className="mb-4">
            <div className="d-flex justify-content-between mb-1">
              <span className="small fw-medium  text-light">Progress</span>
              <span className="small fw-medium  text-light">
                {progressPercentage.toFixed(1)}%
              </span>
            </div>
            <div
              className="progress"
              style={{
                height: "0.75rem",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <div
                className={`progress-bar ${getProgressBarColor(round.status)}`}
                role="progressbar"
                style={{
                  width: `${progressPercentage}%`,
                  position: "relative",
                  overflow: "hidden",
                }}
                aria-valuenow={progressPercentage}
                aria-valuemin="0"
                aria-valuemax="100"
              >
                <div
                  className="progress-flow-light"
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background:
                      "linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.6) 50%, rgba(255,255,255,0) 100%)",
                    animation: "flowLight 1.5s infinite linear",
                    transform: "translateX(-100%)",
                    pointerEvents: "none",
                    width: "50%",
                  }}
                />
              </div>
            </div>

            <div className="d-flex justify-content-between mt-1">
              <span className="small  text-light">
                {round.soldTokens.toLocaleString()} tokens sold
              </span>
              <span className="small  text-light">
                {round.totalTokens.toLocaleString()} total
              </span>
            </div>
          </div>

          {round.status === "active" && (
            <div className="mt-4">
              <div className="d-flex flex-column gap-2">
                {isWalletConnected && (
                  <>
                    <input
                      type="number"
                      placeholder="Amount of tokens"
                      className="form-control"
                      value={amount}
                      onChange={handleAmountChange}
                      min="0"
                      step="1"
                      disabled={!isWalletConnected}
                    />
                    {error && (
                      <div className="text-danger small mt-1">{error}</div>
                    )}
                    {amount && !error && (
                      <div className="text-light small mt-1">
                        Total cost: {(Number(amount) * round.price).toFixed(2)}{" "}
                        USDT
                      </div>
                    )}
                    <div className="d-flex gap-2 mt-2">
                      <div
                        className="border border-success p-2 rounded text-success flex-grow-1 text-center hover-effect"
                        style={{
                          cursor: "pointer",
                          transition: "all 0.2s ease-in-out",
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.classList.add("bg-success");
                          e.currentTarget.classList.add("text-white");
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.classList.remove("bg-success");
                          e.currentTarget.classList.remove("text-white");
                        }}
                        onClick={() => {
                          setAmount("100");
                          setError("");
                          if (10 > round.totalTokens - round.soldTokens) {
                            setError(
                              `Maximum available tokens: ${(
                                round.totalTokens - round.soldTokens
                              ).toLocaleString()}`
                            );
                          }
                        }}
                        disabled={!isWalletConnected}
                      >
                        100 Tokens
                      </div>
                      <div
                        className="border border-success p-2 rounded text-success flex-grow-1 text-center hover-effect"
                        style={{
                          cursor: "pointer",
                          transition: "all 0.2s ease-in-out",
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.classList.add("bg-success");
                          e.currentTarget.classList.add("text-white");
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.classList.remove("bg-success");
                          e.currentTarget.classList.remove("text-white");
                        }}
                        onClick={() => {
                          setAmount("1000");
                          setError("");
                          if (100 > round.totalTokens - round.soldTokens) {
                            setError(
                              `Maximum available tokens: ${(
                                round.totalTokens - round.soldTokens
                              ).toLocaleString()}`
                            );
                          }
                        }}
                        disabled={!isWalletConnected}
                      >
                        1000 Tokens
                      </div>
                      <div
                        className="border border-success p-2 rounded text-success flex-grow-1 text-center hover-effect"
                        style={{
                          cursor: "pointer",
                          transition: "all 0.2s ease-in-out",
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.classList.add("bg-success");
                          e.currentTarget.classList.add("text-white");
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.classList.remove("bg-success");
                          e.currentTarget.classList.remove("text-white");
                        }}
                        onClick={() => {
                          setAmount(
                            (round.totalTokens - round.soldTokens).toString()
                          );
                          setError("");
                        }}
                        disabled={!isWalletConnected}
                      >
                        Max
                      </div>
                    </div>
                  </>
                )}

                <div
                  className={`mt-4 bg-success p-2 text-center rounded text-white ${
                    !isWalletConnected ||
                    !amount ||
                    error ||
                    isPending ||
                    isConfirming
                      ? "opacity-50"
                      : ""
                  }`}
                  style={{ cursor: "pointer" }}
                  onClick={
                    isWalletConnected &&
                    amount &&
                    !error &&
                    !isPending &&
                    !isConfirming
                      ? handleBuy
                      : undefined
                  }
                  disabled={
                    !isWalletConnected ||
                    !amount ||
                    error ||
                    isPending ||
                    isConfirming
                  }
                >
                  {isPending || isConfirming ? (
                    <span>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      {isPending && "Confirming from Wallet..."}
                      {isConfirming && "Confirming from Transaction..."}
                    </span>
                  ) : amount ? (
                    `Buy ${amount} Tokens for ${(
                      Number(amount) * round.price
                    ).toFixed(2)} USDT`
                  ) : (
                    "Buy Tokens"
                  )}
                </div>
              </div>
              {!isWalletConnected && (
                <p className="text-danger small mt-2 text-center">
                  Connect wallet to participate
                </p>
              )}
            </div>
          )}

          {round.status === "upcoming" && (
            <div className="mt-4 bg-primary p-2 text-center rounded">
              <p className="text-white mb-0">Coming soon</p>
            </div>
          )}

          {round.status === "ended" && (
            <div className="mt-4 bg-secondary p-2 text-center rounded">
              <p className="text-white mb-0">This round has ended</p>
            </div>
          )}
        </div>
      </div>{" "}
    </>
  );
};

export default PresaleRound;
